import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  cn,
} from "@nexus/ui";
import { vendorDetailsQueryOptions, useConnectVendor } from "@/lib/queries";
import type { TenantVendorConnection } from "../../types/marketplace.types";
import { useConnectWizardStore } from "../../store/connectWizard.store";
import { isValidSignerName } from "../../lib/signer-name";
import { WizardStep1Package } from "./WizardStep1Package";
import { WizardStep2Terms } from "./WizardStep2Terms";
import { WizardStep3Questions } from "./WizardStep3Questions";
import { WizardStepper } from "./WizardStepper";
import { WizardNav } from "./WizardNav";
import { WizardSuccess } from "./WizardSuccess";

/** How long the success animation plays before the dialog closes. */
const SUCCESS_HOLD_MS = 5000;

interface ConnectWizardProps {
  vendorId: string;
  tenantId: string;
  open: boolean;
  onClose: () => void;
  /** Fires immediately after a connection is created (before the success hold). */
  onConnectSuccess?: (connection: TenantVendorConnection) => void;
}

const TOTAL_STEPS = 3;
const STEP_KEYS = ["package", "terms", "questions"] as const;

export function ConnectWizard({
  vendorId,
  tenantId,
  open,
  onClose,
  onConnectSuccess,
}: ConnectWizardProps) {
  const { t } = useTranslation();
  const { data: vendor } = useQuery(vendorDetailsQueryOptions(vendorId));
  const store = useConnectWizardStore();
  const connectMutation = useConnectVendor();
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (!showSuccess) return;
    const timer = setTimeout(onClose, SUCCESS_HOLD_MS);
    return () => clearTimeout(timer);
  }, [showSuccess, onClose]);

  useEffect(() => {
    if (!open) setShowSuccess(false);
  }, [open]);

  const activePackages = vendor?.packages.filter((p) => p.isActive) ?? [];

  function isAnswerFilled(type: string, raw: string | undefined): boolean {
    if (!raw) return false;
    if (type === "SELECT") {
      try { return (JSON.parse(raw) as string[]).length > 0; } catch { return false; }
    }
    return !!raw.trim();
  }

  const canAdvanceStep1 =
    vendor !== undefined &&
    (activePackages.length === 0 || store.selectedPackageId !== null);
  const canAdvanceStep2 =
    store.termsScrolled &&
    store.termsAccepted &&
    isValidSignerName(store.signerName);
  const canSubmitStep3 =
    !vendor?.questions.some(
      (q) => q.isRequired && !isAnswerFilled(q.type, store.answers[q.id]),
    );

  const canAdvance =
    store.step === 1
      ? canAdvanceStep1
      : store.step === 2
        ? canAdvanceStep2
        : (canSubmitStep3 ?? false);

  async function handleNext() {
    if (store.step < TOTAL_STEPS) {
      store.nextStep();
      return;
    }
    if (!vendor) return;

    const answers = vendor.questions
      .filter((q) => isAnswerFilled(q.type, store.answers[q.id]))
      .map((q) => ({ questionId: q.id, value: store.answers[q.id] }));

    const connection = await connectMutation.mutateAsync({
      tenantId,
      vendorId,
      packageId: store.selectedPackageId ?? undefined,
      signerName: store.signerName.trim(),
      answers,
    });
    onConnectSuccess?.(connection);
    setShowSuccess(true);
  }

  const stepKey = STEP_KEYS[store.step - 1];

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !o && !connectMutation.isPending && onClose()}
    >
      <DialogContent
        variant="form"
        className="flex h-[780px] max-h-[92dvh] flex-col gap-0 overflow-hidden p-0 sm:max-w-[1040px]"
      >
        {showSuccess ? (
          <>
            <DialogTitle className="sr-only">
              {t("marketplace.wizard.success.title", {
                vendorName: vendor?.name ?? "",
              })}
            </DialogTitle>
            <WizardSuccess vendorName={vendor?.name ?? ""} />
          </>
        ) : (
          <>
            <DialogHeader className="border-b border-border px-6 py-5 pr-12">
          <DialogTitle>
            {vendor
              ? t("marketplace.wizard.title", { vendorName: vendor.name })
              : t("marketplace.wizard.titleLoading")}
          </DialogTitle>
          <DialogDescription>
            {t(`marketplace.wizard.subtitles.${stepKey}`)}
          </DialogDescription>
        </DialogHeader>

        <WizardStepper step={store.step} />

        <div className="flex flex-1 flex-col overflow-y-auto px-6 py-6">
          {vendor && (
            <div
              key={store.step}
              className={cn(
                "animate-in fade-in-0 slide-in-from-bottom-2 duration-300 motion-reduce:animate-none",
                store.step === 2 ? "h-full w-full" : "my-auto",
                store.step === 3 && "mx-auto w-full max-w-[640px]",
              )}
            >
              {store.step === 1 && <WizardStep1Package vendor={vendor} />}
              {store.step === 2 && <WizardStep2Terms vendor={vendor} />}
              {store.step === 3 && <WizardStep3Questions vendor={vendor} />}
            </div>
          )}
        </div>

            <WizardNav
              step={store.step}
              canAdvance={canAdvance}
              isSubmitting={connectMutation.isPending}
              onBack={store.prevStep}
              onNext={() => void handleNext()}
              onCancel={onClose}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
